import React, { useState, useEffect, useRef, useCallback } from "react";
import "../styles/BubbleTimer.css"; // Add styles for the box and timer
import { useFilters, useTimer, useTasks, useUI } from "../hooks/index.js";
import { baseBackendURL } from "../helpers/baseUrl.js";

const BubbleTimer = () => {
  const { activeSlate } = useFilters();
  const {
    elapsedTime,
    timer,
    activeBubble,
    setElapsedTime,
    setTimer,
    setActiveBubble,
  } = useTimer();
  const { setTasks, setReturnedBubbles, returnedBubbles } = useTasks();
  const { isMobile } = useUI();
  const [isDraggingOver, setIsDraggingOver] = useState(false); // Track drag-over status
  const dropZoneRef = useRef(null);

  const handleReturnBubble = useCallback(
    (bubble) => {
      setReturnedBubbles([...returnedBubbles, bubble]);
      setTasks((prevTasks) =>
        prevTasks.map((task) =>
          task.task_id === bubble.task_id
            ? { ...task, x: bubble.x, y: bubble.y, urgent: task.urgent }
            : task
        )
      );
    },
    [returnedBubbles, setReturnedBubbles, setTasks]
  );

  const startTimer = (initialTime = 0) => {
    if (isNaN(initialTime)) initialTime = 0;
    const startTime = Date.now() - initialTime * 1000;
    const timerInterval = setInterval(() => {
      const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
      setElapsedTime(elapsedSeconds);
    }, 1000);
    setTimer(timerInterval);
  };

  const resetTimer = () => {
    if (timer) clearInterval(timer);
    setTimer(null);
    setElapsedTime(0);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDraggingOver(false);

    try {
      const bubbleData = JSON.parse(
        event.dataTransfer
          ? event.dataTransfer.getData("bubble")
          : event.target.dataset.bubble
      );
      setActiveBubble(bubbleData);
      startTimer(bubbleData.minutes ? bubbleData.minutes * 60 : 0);
    } catch (error) {
      console.error("Error handling drop:", error);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDraggingOver(true);
  };

  const handleDragLeave = () => {
    setIsDraggingOver(false);
  };

  // Format elapsedTime for display
  const formattedTime = () => {
    if (elapsedTime < 60) return `${elapsedTime}s`;
    if (elapsedTime < 600)
      return `${Math.floor(elapsedTime / 60)}m ${elapsedTime % 60}s`;
    if (elapsedTime < 3600) return `${Math.floor(elapsedTime / 60)}m`;
    return `${Math.floor(elapsedTime / 3600)}h ${Math.floor(
      (elapsedTime % 3600) / 60
    )}m`;
  };

  useEffect(() => {
    const restoreState = async () => {
      const savedTask = JSON.parse(
        localStorage.getItem(`activeTask_${activeSlate}`)
      );
      if (savedTask) {
        setActiveBubble(savedTask.task);
        setElapsedTime(savedTask.elapsedSeconds);
        startTimer(savedTask.elapsedSeconds);
      } else {
        await fetchActiveTask(activeSlate);
      }
    };

    restoreState();
  }, [activeSlate]);

  useEffect(() => {
    if (activeBubble) {
      localStorage.setItem(
        `activeTask_${activeSlate}`,
        JSON.stringify({
          task: activeBubble,
          elapsedSeconds: elapsedTime,
        })
      );
    } else {
      localStorage.removeItem(`activeTask_${activeSlate}`);
    }
  }, [activeBubble, elapsedTime, activeSlate]);

  useEffect(() => {
    const dropZoneElement = dropZoneRef.current;
    if (!dropZoneElement) return;

    const handleTouchMove = (e) => {
      const { clientX, clientY, bubble } = e.detail;
      const rect = dropZoneElement.getBoundingClientRect();

      if (
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom
      ) {
        setIsDraggingOver(true);
      } else {
        setIsDraggingOver(false);
      }
    };

    const handleTouchEnd = (e) => {
      const { clientX, clientY, bubble } = e.detail;
      const rect = dropZoneElement.getBoundingClientRect();

      if (
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom
      ) {
        setIsDraggingOver(false);

        try {
          setActiveBubble(bubble);
          startTimer(bubble.minutes ? bubble.minutes * 60 : 0);
        } catch (error) {
          console.error("Error handling touch drop:", error);
        }
      }

      setIsDraggingOver(false);
    };

    document.addEventListener("bubbleTouchMove", handleTouchMove);
    document.addEventListener("bubbleTouchEnd", handleTouchEnd);

    return () => {
      document.removeEventListener("bubbleTouchMove", handleTouchMove);
      document.removeEventListener("bubbleTouchEnd", handleTouchEnd);
    };
  }, []);

  const stopTask = async () => {
    if (activeBubble) {
      const roundedMinutes = Math.round(elapsedTime / 60);
      try {
        const response = await fetch(`${baseBackendURL}/tasktiming`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            task_id: activeBubble.task_id,
            minutes: roundedMinutes,
            isActive: false,
          }),
          credentials: "include",
        });

        if (response.ok) {
          // Remove active bubble from local storage
          localStorage.removeItem(`activeTask_${activeSlate}`);
          // Update the task in the tasks array
          setTasks((prevTasks) =>
            prevTasks.map((task) =>
              task.task_id === activeBubble.task_id
                ? { ...task, minutes: roundedMinutes }
                : task
            )
          );

          handleReturnBubble(activeBubble);
          setActiveBubble(null);
          resetTimer();
        } else {
          console.error("Failed to stop task:", await response.text());
        }
      } catch (err) {
        console.error("Failed to stop task:", err);
      }
    }
  };

  const fetchActiveTask = async (slate) => {
    try {
      const response = await fetch(
        `${baseBackendURL}/tasktiming?slate=${slate}`,
        {
          credentials: "include",
        }
      );
      const data = await response.json();
      if (data.success && data.task) {
        setActiveBubble(data.task);
        setElapsedTime(data.elapsed_seconds || 0);
        startTimer(data.elapsed_seconds || 0);
      }
    } catch (error) {
      console.error("Error fetching active task:", error);
    }
  };
  return (
    <div
      className={`bubble-timer-box ${
        isDraggingOver ? "dragging-over" : activeBubble ? "active" : ""
      }`}
      ref={dropZoneRef}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onTouchEnd={() => stopTask(activeBubble.task_id, elapsedTime)}
      onDoubleClick={() => stopTask(activeBubble.task_id, elapsedTime)}
    >
      {/* Active Timing */}
      {activeBubble && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            opacity: 0.5,
            borderRadius: "50%",
            backgroundImage: "url('/media/timer.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            animation: "spin 2s linear infinite",
          }}
        ></div>
      )}

      {/* Placeholder content */}
      {activeBubble ? (
        <div
          title={activeBubble.task_name}
          className="timing-content"
          style={{ position: "relative", zIndex: 1 }}
        >
          <p>Time on Task: {formattedTime()}</p>
        </div>
      ) : (
        <div
          className="timer-content"
          style={{ position: "relative", zIndex: 1 }}
        >
          {isDraggingOver ? (
            <p className="drop-text">Bubble, Set, Go...</p>
          ) : (
            <>
              <img
                src="/media/timer.png"
                alt="Timer Icon"
                style={{
                  width: "40px",
                  height: "40px",
                  position: "relative",
                  zIndex: 1,
                }}
              />
              <p className="target-text">Task Timer</p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default BubbleTimer;
